<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";

import { Menu } from 'primevue';
import { useRouter } from 'vue-router';
import { useLayout } from '@/layout/composables/layout';
import { useAuthStore } from '@/stores/authStore';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useProfileStore } from "@/stores/profileStore";
import { storeToRefs } from 'pinia';
import { orgInitials } from '@/utils/orgInitials';
import ProfileModal from "../profile/ProfileModal.vue";
import OrgSwitcherDialog from './OrgSwitcherDialog.vue';

const { layoutState, isSlim } = useLayout();
const router = useRouter();
const authStore = useAuthStore()
const profileStore = useProfileStore();
const { isFeatureEnabled } = useFeatureFlags();

const { logoUrl } = storeToRefs(authStore);

const menu = ref();
const menuVisible = ref(false);
const switcherVisible = ref(false);

// Org switcher: only for users with admin access to more than one organization. The menu used to
// list every organization inline, which grew with each one and ran straight into the account
// items; now one row names the current organization and opens OrgSwitcherDialog.
const canSwitchOrganization = computed(() => authStore.adminOrganizations.length > 1);

// The token's org_name claim, falling back to the organizations list for a session that
// restored its org id from storage without a name.
const currentOrgName = computed(
    () =>
        authStore.currentOrg ||
        authStore.adminOrganizations.find((org) => org.orgCode === authStore.currentOrgId)?.name ||
        authStore.currentOrgId
);

const items = computed(() => {
    const menuItems: any[] = [];

    if (canSwitchOrganization.value) {
        menuItems.push(
            {
                label: currentOrgName.value,
                orgRow: true,
                command: () => {
                    switcherVisible.value = true;
                }
            },
            { separator: true }
        );
    }

    // Org configuration lives in the sidebar now (Administration > Settings), so this
    // menu holds identity and the org-level surfaces that have no page of their own.
    menuItems.push(
        {
            label: 'Profile',
            icon: 'pi pi-user',
            command: () => {
                profileStore.openProfileModal();
            }
        },
        {
            label: 'Billing',
            icon: 'pi pi-credit-card',
            command: () => {
                goToBilling();
            }
        },
    );

    if (isFeatureEnabled('integrations', false)) {
        menuItems.push({
            label: 'Integrations',
            icon: 'pi pi-microchip',
            route: '/integrations',
        });
    }

    menuItems.push(
        {
            label: 'SQL Connector',
            icon: 'pi pi-database',
            route: '/docs/sql-connector',
        },
        { separator: true },
        {
            label: 'Sign Out',
            icon: 'pi pi-power-off',
            command: () => {
                logout();
            }
        }
    );

    return menuItems;
});

onMounted(() => {
    authStore.getLogoUrl();
    authStore.getOrganizations();
})

// Billing is one page now: subscription, seats and invoices together, rather than the seat control
// sitting on the reps page and the history somewhere else.
const goToBilling = () => {
    layoutState.profileSidebarVisible = false;
    router.push({ path: '/billing' });
}

const toggle = (event: Event) => {
    menu.value.toggle(event);
    menuVisible.value = menu.value.overlayVisible;
};

const logout = async () => {
    await authStore.logout();
}
</script>

<template>
    <div :class="`${$style.sidebarAccountMenu} ${isSlim ? $style.slim : ''}`">
        <ProfileModal />


        <Transition name="menuLogo" mode="out-in">
            <Button v-if="isSlim" :class="$style.menuBtn" type="button" severity="secondary" @click="toggle" aria-haspopup="true" aria-controls="overlay_menu" >
                <div class="flex items-center">
                    <div v-if="logoUrl" :class="`${$style.logoWrap}`">
                        <img :src="logoUrl" :alt="authStore.user?.given_name" class="p-1.5" />
                    </div>
                    <i v-else class="pi pi-user"></i>
                </div>
            </Button>
            <Button v-else :class="$style.menuBtn" type="button" severity="secondary" @click="toggle" aria-haspopup="true" aria-controls="overlay_menu">
                <div class="flex items-center">
                    <div v-if="logoUrl" :class="$style.logoWrap">
                        <img :src="logoUrl" :alt="authStore.user?.given_name" class="p-1.5" />
                    </div>
                    <i v-else class="pi pi-user pl-2 pr-4"></i>
                    <span  class="">{{ authStore.user?.given_name }} {{ authStore.user?.family_name }} 
                        <br/>{{authStore.currentOrg}}
                    </span>
                </div>
                <i :class="`pi pi-chevron-up ${$style.menuArrow} ${menuVisible ? $style.spin : ''}`"></i>
            </Button>

        </Transition>


        <OrgSwitcherDialog v-if="canSwitchOrganization" v-model:visible="switcherVisible" />

        <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" :class="$style.menu">
            <template #item="{ item, props }">
                <div v-if="item.orgRow" v-bind="props.action" :class="$style.orgRow">
                    <span :class="$style.orgAvatar" aria-hidden="true">{{ orgInitials(item.label) }}</span>
                    <span :class="$style.orgText">
                        <span :class="$style.orgName">{{ item.label }}</span>
                        <span :class="$style.orgHint">Switch organization</span>
                    </span>
                    <i class="pi pi-chevron-right" :class="$style.orgChevron" aria-hidden="true"></i>
                </div>
                <router-link v-else-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
                    <a :href="href" v-bind="props.action" @click="navigate">
                        <span :class="item.icon" />
                        <span class="ml-2">{{ item.label }}</span>
                    </a>
                </router-link>
                <div v-else-if="item.command" v-bind="props.action">
                    <span :class="item.icon" />
                    <span class="ml-2">{{ item.label }}</span>
                </div>
            </template>
        </Menu>
    </div>
</template>

<style module>
.sidebarAccountMenu {
    @apply w-full flex justify-center items-center py-4 px-4;

    &.slim {
        @apply px-0;
    }
}

.menuBtn {
    @apply w-full justify-between items-center pl-1 pr-3;
}

.menuArrow {
    @apply transition-transform duration-300 ease-in-out;

    &.spin {
        @apply rotate-180;
    }
}

/* Wide enough for the organization row's two lines; the popup is anchored to the trigger's left
   edge, so the extra width runs out over the page rather than off the sidebar. */
.menu {
    width: 248px;
}

.orgRow {
    gap: 10px;
}

.orgAvatar {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    background: var(--tally-brand-navy);
    color: var(--tally-text-inverse);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
}

.orgText {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.3;
}

.orgName {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.orgHint {
    font-size: 11px;
    color: var(--tally-accent-blue);
}

.orgChevron {
    margin-left: auto;
    font-size: 11px;
    color: var(--tally-text-nav);
}

.logoWrap {
    @apply w-14 h-14 min-w-14 rounded-full mr-3 overflow-hidden border p-1.5 flex items-center justify-center;
    img{
        @apply max-h-14;
    }
}

.slim {
    .menuBtn {
        @apply px-1 justify-center w-auto hover:bg-transparent hover:border-transparent ;
    }
    .logoWrap{
        @apply mr-0 hover:bg-white ;
    }
}
</style>